import {useCallback, useEffect, useMemo, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {usePageProps} from 'context/pageProps'
import {differenceInDays} from 'date-fns/differenceInDays'
import {format} from 'date-fns/format'
import {parseISO} from 'date-fns/parseISO'
import {useDatelessSeoSRP} from 'hooks/useDatelessSeoSRP'
import {
  setSearchProgressPercentage,
  setSearchProgressPercentageIncrementally
} from 'modules/progressBar/slice'
import {SearchProgressPercentage} from 'modules/progressBar/types'
import {getUrlParams, getUseBoVioFirstProfile} from 'modules/search/selectors'
import {setAudienceSegmentationAttributes} from 'opticks'
import {omit} from 'ramda'
import {scrollTo} from 'utils'
import {ensureCheckInOnOrAfterToday, isValidSapiSearchParameters} from 'utils'

import {BOVIO_FIRST_PROFILE} from '@vio/core/src/profile/constants'
import {getInitialSearchDates} from '@vio/core/src/searchParams/validateSearchParamDates'

import {loadMoreResults, runSplitSearch} from './actions'
import {URL_PARAMS_TO_IGNORE_FOR_SEARCHES} from './constants'
import {useHandleSearchError, useSapi} from './SapiProvider'
import {
  getHotelAvailabilityPricesComplete,
  getInputSearchParameters,
  getModuleState,
  getPriceDisplaySearchKey,
  getSearchParameters
} from './selectors'
import {
  hotelsAvailabilityCompleted,
  hotelsAvailabilityReceived,
  hotelsAvailabilityRequested,
  ModuleState
} from './slice'
import {getSearchTypeFromSearchParameters} from './utils'

import type {AnchorPreheatOptions} from './partialSearch'
import type {ApiSearchParameters as SapiSearchParameters} from '@findhotel/sapi'
import type {PageProps as SearchPageProps} from 'pages/Hotel/Search'

/**
 * Initialize the Search for SRP and start listening for URL / parameter changes
 */
export const useInitSearch = () => {
  const isDatelessSeoSRP = useDatelessSeoSRP()
  const {sapiClient} = useSapi()
  const handleSearchError = useHandleSearchError()
  const dispatch = useDispatch()
  const searchParams = useSelector(getInputSearchParameters)
  const urlParams = useSelector(getUrlParams)
  const priceDisplaySearchKey = useSelector(getPriceDisplaySearchKey)

  const moduleState = useSelector(getModuleState)
  const hotelAvailabilityPricesComplete = useSelector(
    getHotelAvailabilityPricesComplete
  )

  const runSearch = useCallback(
    async (
      searchParameters: SapiSearchParameters,
      hasPreloadedHotels: boolean,
      preloadedAnchorOptions?: AnchorPreheatOptions
    ) => {
      scrollTo(document.querySelector('html'), 0, 0)

      try {
        await dispatch(
          runSplitSearch({
            searchParameters,
            hasPreloadedHotels,
            ...(preloadedAnchorOptions ? {preloadedAnchorOptions} : {})
          })
        )
      } catch (error) {
        handleSearchError(error)
      }
    },
    [dispatch, handleSearchError]
  )

  // A new search may be started mid-search so we cannot just look at progress state
  const {preloadedReduxState, preloadedAnchorOptions} =
    usePageProps() as SearchPageProps
  const preloadedAnchorOptionsRef = useRef(preloadedAnchorOptions)
  const hasPreloadedResultsRef = useRef(
    preloadedReduxState?.sapiSearch?.moduleState == ModuleState.OffersPending
  )

  // This is instead of doing a Deep equality check we pass a stringified version to the useEffect
  // this is added because we don't want to trigger runSearch when we change 'layout' in the URL
  // see: https://github.com/facebook/react/issues/14476#issuecomment-471199055
  const stringifiedUrlParams = JSON.stringify(
    omit(URL_PARAMS_TO_IGNORE_FOR_SEARCHES, urlParams)
  )
  const initialUrlParamsRef = useRef(stringifiedUrlParams)
  const stringifiedCugDeals = JSON.stringify(searchParams?.cugDeals)
  const tier = searchParams?.tier
  const stringifiedGeolocation = JSON.stringify(searchParams?.geolocation)
  const isSapiReady = !!sapiClient

  useEffect(() => {
    const hasPreloadedHotels = hasPreloadedResultsRef.current
    const anchorOptions =
      initialUrlParamsRef.current === stringifiedUrlParams
        ? preloadedAnchorOptionsRef.current
        : undefined

    // Skip first-load client-side search completely for dateless SEO SRP
    const skipSearch = isDatelessSeoSRP && hasPreloadedHotels

    if (
      isSapiReady &&
      tier !== undefined &&
      searchParams &&
      isValidSapiSearchParameters(searchParams) &&
      !skipSearch
    ) {
      // Consume server results once
      hasPreloadedResultsRef.current = false
      preloadedAnchorOptionsRef.current = undefined

      runSearch(searchParams, hasPreloadedHotels, anchorOptions)
    }
  }, [
    isSapiReady,
    runSearch,
    stringifiedUrlParams,
    stringifiedCugDeals,
    tier,
    priceDisplaySearchKey,
    stringifiedGeolocation,
    isDatelessSeoSRP
  ])

  // TODO (@Search) [2021-08-30] Move segmentation to a separate component
  useEffect(() => {
    setAudienceSegmentationAttributes({
      searchType: getSearchTypeFromSearchParameters(searchParams)
    })
  }, [searchParams])

  // completes search progress bar when the search and availability requests are completed
  useEffect(() => {
    if (
      hotelAvailabilityPricesComplete &&
      moduleState == ModuleState.SearchCompleted
    )
      dispatch(
        setSearchProgressPercentage(SearchProgressPercentage.SearchCompleted)
      )
  }, [hotelAvailabilityPricesComplete, moduleState, dispatch])
}

/**
 * Load more results for the current ongoing search
 */
const useLoadMoreResults = () => {
  const dispatch = useDispatch()
  return useCallback(
    (paginate = true) => {
      dispatch(loadMoreResults({paginate}))
    },
    [dispatch]
  )
}

const useGetHotelAvailability = ({hotelId}: {hotelId: string}) => {
  const dispatch = useDispatch()
  const {searchHotelAvailability} = useSapi()
  const boVioFirstProfileIsEnabled = useSelector(getUseBoVioFirstProfile)

  const searchParameters = useSelector(getSearchParameters)
  const {rooms, cugDeals, tier, originId} = searchParameters || {}

  const {checkIn, checkOut} = searchParameters || {}
  const nights =
    checkIn &&
    checkOut &&
    differenceInDays(
      parseISO(searchParameters.checkOut),
      parseISO(searchParameters.checkIn)
    )

  const {startDate, endDate} = useMemo(
    () => getInitialSearchDates(checkIn),
    [checkIn]
  )

  const availabilityCallbacks = useMemo(
    () => ({
      onStart: () => {
        dispatch(hotelsAvailabilityRequested())
      },
      onAvailabilityReceived: response => {
        dispatch(
          setSearchProgressPercentageIncrementally(
            SearchProgressPercentage.OffersReceived
          )
        )
        dispatch(hotelsAvailabilityReceived(response))
      },
      onComplete: response => {
        dispatch(hotelsAvailabilityCompleted(response))
      }
    }),
    [dispatch]
  )

  const initialParams = useMemo(
    () => ({
      nights,
      hotelIds: hotelId,
      rooms,
      originId: boVioFirstProfileIsEnabled
        ? (BOVIO_FIRST_PROFILE ?? '')
        : originId,
      cugDeals,
      tier: tier ?? ''
    }),
    [
      cugDeals,
      hotelId,
      nights,
      originId,
      rooms,
      tier,
      boVioFirstProfileIsEnabled
    ]
  )

  const SAPI_VALID_DATE_FORMAT = 'yyyy-MM-dd'

  const getHotelAvailabilityForCurrentSearch = useCallback(async () => {
    const parameters = {
      startDate: format(startDate, SAPI_VALID_DATE_FORMAT),
      endDate: format(endDate, SAPI_VALID_DATE_FORMAT),
      ...initialParams
    }
    searchHotelAvailability(parameters, availabilityCallbacks)
  }, [
    availabilityCallbacks,
    endDate,
    initialParams,
    searchHotelAvailability,
    startDate
  ])

  const getMonthlyAvailability = useCallback(
    async (date: Date) => {
      const startDate = ensureCheckInOnOrAfterToday(
        new Date(date.getFullYear(), date.getMonth(), 1)
      )
      const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0)
      const parameters = {
        startDate: format(startDate, SAPI_VALID_DATE_FORMAT),
        endDate: format(endDate, SAPI_VALID_DATE_FORMAT),
        ...initialParams
      }
      searchHotelAvailability(parameters, availabilityCallbacks)
    },
    [initialParams, searchHotelAvailability, availabilityCallbacks]
  )

  return {getMonthlyAvailability, getHotelAvailabilityForCurrentSearch}
}

export const useSapiSearch = () => ({
  loadMoreResults: useLoadMoreResults(),
  useGetHotelAvailability
})
